/**
 * Container for the role chip grid component
 *
 * Manages expanded/collapsed state and pre-builds stable toggle handlers
 * for each role option. Delegates presentation to RoleChipGridView.
 * @module features/scouting/components/RoleChipGrid/RoleChipGridContainer
 */

import { useCallback, useMemo, useState } from "react";

import RoleChipGridView, { type RoleOption } from "./RoleChipGridView";

import { useTheme } from "@/providers/ThemeProvider";

/** Number of role chips visible before "Show more" */
const DEFAULT_VISIBLE_COUNT = 6;

/** Props for RoleChipGridContainer */
interface RoleChipGridContainerProps {
  /** Available role options */
  readonly options: readonly RoleOption[];
  /** Currently selected role IDs */
  readonly selectedIds: readonly string[];
  /** Callback when a role is toggled */
  readonly onToggleOption: (id: string) => void;
}

/**
 * Manages role chip grid expand state and pre-builds option handlers
 * @param root0 - Component props
 * @param root0.options - Available role options
 * @param root0.selectedIds - Currently selected role IDs
 * @param root0.onToggleOption - Toggle callback
 */
const RoleChipGridContainer = ({
  options,
  selectedIds,
  onToggleOption,
}: RoleChipGridContainerProps) => {
  const isDark = useTheme().resolvedTheme === "dark";
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const onToggleExpanded = useCallback(() => setIsExpanded(prev => !prev), []);

  const onHoverIn = useCallback((id: string) => setHoveredId(id), []);
  const onHoverOut = useCallback(() => setHoveredId(null), []);

  const optionHandlers = useMemo(
    () =>
      options.reduce(
        (acc, option) => ({
          ...acc,
          [option.id]: () => onToggleOption(option.id),
        }),
        {} as Record<string, () => void>
      ),
    [options, onToggleOption]
  );

  const hasMore = options.length > DEFAULT_VISIBLE_COUNT;

  const visibleOptions = useMemo(
    () => (isExpanded ? options : options.slice(0, DEFAULT_VISIBLE_COUNT)),
    [isExpanded, options]
  );

  return (
    <RoleChipGridView
      visibleOptions={visibleOptions}
      selectedIds={selectedIds}
      optionHandlers={optionHandlers}
      isExpanded={isExpanded}
      onToggleExpanded={onToggleExpanded}
      hasMore={hasMore}
      isDark={isDark}
      hoveredId={hoveredId}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
    />
  );
};

export default RoleChipGridContainer;
